import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatUsd } from '../lib/api';
import { Button, Card, Alert, Modal, Field, Input, Select } from './ui';

/**
 * Send a test stream from the hub (server: services/test-streams.ts): a test
 * pattern or a recording, as it is, to SRT and RTMP destinations. The button
 * lives in the Encoders page's header; while one runs, a card at the top of
 * the page shows it. docs/tools/test-streams.md.
 */

type Protocol = 'srt-caller' | 'srt-listener' | 'rtmp';
interface Destination { id?: string; label: string; protocol: Protocol; host: string; port: number | ''; streamId: string; passphrase: string; latencyMs: number; url: string }
type Source = { kind: 'pattern'; id: string } | { kind: 'recording'; instanceId: string; session: string };
interface Spec { source: Source; destinations: Destination[]; loop: boolean; stopAfterHours: number | null }
interface DestState { id: string; state: 'starting' | 'sending' | 'failed' | 'listening'; note: string | null; lastErrorAt: string | null }
interface Stream {
  id: string; spec: Spec; source: { name: string; mbps: number; seconds: number | null };
  status: 'starting' | 'running' | 'stopped'; progress: string | null;
  startedAt: string; stopAt: string | null; stoppedAt: string | null; stoppedWhy: string | null;
  by: { id: string; email: string }; restarts: number;
  stats: { at: string; speed: number; outTimeMs: number } | null;
  destinations: DestState[]; log: string[]; egress: { gb: number; usd: number };
}
interface Payload {
  current: Stream | null; last: Stream | null;
  patterns: { id: string; name: string; mbps: number; ready: boolean }[];
  limits: { maxDestinations: number; maxMbps: number; listenerPorts: [number, number]; maxHours: number };
  egressUsdPerGb: number; listenHost: string;
}
interface RecordingRow { instanceId: string; session: string; prefix: string; title?: string; source: string; seconds: number; recording?: boolean; machineName?: string; origin?: { name: string } }

const newDest = (n: number): Destination => ({ label: `Destination ${n}`, protocol: 'srt-caller', host: '', port: '', streamId: '', passphrase: '', latencyMs: 1000, url: '' });
const hms = (ms: number) => { const s = Math.floor(ms / 1000); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return `${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${String(s % 60).padStart(2, '0')}`; };
const since = (iso: string) => hms(Date.now() - Date.parse(iso));
const STATE_COLOUR: Record<DestState['state'], string> = { starting: 'var(--color-warning)', sending: 'var(--color-good)', listening: 'var(--color-series-1)', failed: 'var(--color-critical)' };

export function useTestStreams() {
  const [data, setData] = useState<Payload | null>(null);
  const load = useCallback(() => api.get<Payload>('/test-streams').then(setData).catch(() => undefined), []);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), data?.current ? 2000 : 10_000);
    return () => clearInterval(t);
  }, [load, data?.current ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, load };
}

/** The card at the top of the Encoders page while a test stream runs (or the last one, briefly after). */
export function TestStreamCard({ data, reload, canUse, onEdit }: { data: Payload; reload: () => void; canUse: boolean; onEdit: (spec: Spec) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(t); }, []);

  const s = data.current ?? data.last;
  if (!s) return null;
  const running = s.status !== 'stopped';
  // The last one is shown for ten minutes after it stopped, so a stop timer or a failure is seen.
  if (!running && s.stoppedAt && Date.now() - Date.parse(s.stoppedAt) > 10 * 60_000) return null;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); reload(); } catch (err) { setError(err instanceof ApiError ? err.message : 'That did not work.'); } finally { setBusy(false); }
  };
  const slow = running && s.stats && s.stats.speed > 0 && s.stats.speed < 0.97;

  return (
    <Card
      title={running ? 'Test stream running' : 'Last test stream'}
      action={canUse && (
        <div className="flex gap-1">
          {running && <Button variant="ghost" loading={busy} onClick={() => void act(() => api.post('/test-streams/restart'))}>Restart</Button>}
          <Button variant="ghost" loading={busy} onClick={() => onEdit(s.spec)}>{running ? 'Edit…' : 'Send again…'}</Button>
          {running && <Button variant="danger" loading={busy} onClick={() => void act(() => api.post('/test-streams/stop'))}>Stop</Button>}
        </div>
      )}
    >
      <div className="space-y-3 p-4 text-sm">
        {error && <Alert tone="critical">{error}</Alert>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium" style={{ color: running ? 'var(--color-good)' : 'var(--color-ink-muted)' }}>
            {running ? '● ' : ''}{s.source.name}{s.spec.loop ? ' · looping' : ''}
          </span>
          <span className="text-ink-muted">
            started {new Date(s.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} by {s.by.email.split('@')[0]}
            {running ? ` · running ${since(s.startedAt)}` : ` · stopped ${s.stoppedAt ? new Date(s.stoppedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}: ${s.stoppedWhy ?? ''}`}
            {s.stopAt && running && ` · stops ${new Date(s.stopAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
            {s.restarts > 0 && ` · restarted ${s.restarts}×`}
          </span>
        </div>
        {s.progress && <div className="text-ink-secondary">{s.progress}</div>}
        {s.stats && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-secondary">
            <span>{s.source.mbps} Mbit/s to each destination</span>
            <span className={slow ? 'text-warning' : ''} title="How fast ffmpeg is sending against real time. Under 1.0 for long means the hub or a destination cannot keep up.">speed {s.stats.speed.toFixed(2)}×</span>
            <span>{hms(s.stats.outTimeMs)} sent{s.source.seconds && !s.spec.loop ? ` of ${hms(s.source.seconds * 1000)}` : ''}</span>
            <span title="Data leaving the hub, at EC2's price">{s.egress.gb} GB out · {formatUsd(s.egress.usd)}</span>
          </div>
        )}
        {slow && <Alert tone="warning">Sending slower than real time. A destination may be slow to accept, or the hub's link is short of room: fewer destinations, or a lighter source.</Alert>}
        <div className="divide-y divide-grid rounded border border-grid">
          {s.spec.destinations.map((d) => {
            const st = s.destinations.find((x) => x.id === d.id);
            const where = d.protocol === 'rtmp' ? d.url : d.protocol === 'srt-listener' ? `srt://${data.listenHost}:${d.port}  (listener - the receiver calls this)` : `srt://${d.host}:${d.port}  (caller)`;
            return (
              <div key={d.id} className="flex items-start gap-3 px-3 py-2">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: running && st ? STATE_COLOUR[st.state] : 'var(--color-ink-muted)' }} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{d.label}</span>
                    <span className="text-xs text-ink-muted">{running && st ? st.state : 'stopped'}</span>
                  </div>
                  <div className="truncate font-mono text-xs text-ink-secondary">{where}{d.streamId ? `  streamid=${d.streamId}` : ''}{d.passphrase ? '  passphrase set' : ''}{d.protocol !== 'rtmp' ? `  latency ${d.latencyMs} ms` : ''}</div>
                  {st?.note && <div className={`text-xs ${st.state === 'failed' ? 'text-critical' : 'text-ink-muted'}`}>{st.note}</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
          <button type="button" className="text-series-1 hover:underline" onClick={() => setShowLog((v) => !v)}>{showLog ? 'Hide' : 'Show'} ffmpeg's log</button>
          <span>SRT's far-end loss is not reported by ffmpeg; a destination goes red when ffmpeg complains about it, and it keeps retrying by itself.</span>
        </div>
        {showLog && (
          <pre className="max-h-56 overflow-auto rounded border border-grid bg-plane p-2 font-mono text-[11px] leading-snug text-ink-secondary">{s.log.length ? s.log.join('\n') : 'Nothing yet.'}</pre>
        )}
      </div>
    </Card>
  );
}

/** The modal: pick a source, add destinations, start. */
export function TestStreamDialog({ open, onClose, data, initial, onStarted }: {
  open: boolean; onClose: () => void; data: Payload | null; initial: Spec | null; onStarted: () => void;
}) {
  const [source, setSource] = useState<Source>({ kind: 'pattern', id: 'bars-1080p50' });
  const [dests, setDests] = useState<Destination[]>([newDest(1)]);
  const [loop, setLoop] = useState(true);
  const [hours, setHours] = useState('2');
  const [recordings, setRecordings] = useState<RecordingRow[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setSource(initial.source);
      setDests(initial.destinations.map((d) => ({ ...d, passphrase: d.passphrase.startsWith('•') ? '' : d.passphrase, url: d.url.endsWith('…') ? '' : d.url })));
      setLoop(initial.loop);
      setHours(initial.stopAfterHours ? String(initial.stopAfterHours) : '');
    }
    api.get<{ sessions: RecordingRow[] }>('/recordings').then((r) => setRecordings(r.sessions.filter((s) => !s.recording))).catch(() => setRecordings([]));
  }, [open, initial]);

  const patterns = data?.patterns ?? [];
  const limits = data?.limits ?? { maxDestinations: 4, maxMbps: 40, listenerPorts: [5000, 5009] as [number, number], maxHours: 24 };
  const mbps = source.kind === 'pattern' ? (patterns.find((p) => p.id === source.id)?.mbps ?? 8) : 8;
  const edited = initial ? initial.destinations.some((d) => d.passphrase.startsWith('•') || d.url.endsWith('…')) : false;
  const q = query.trim().toLowerCase();
  const name = (r: RecordingRow) => r.title?.trim() || r.origin?.name || (r.machineName ? `${r.machineName} · ${r.source}` : r.source);
  const shownRecordings = (recordings ?? []).filter((r) => !q || `${name(r)} ${r.session}`.toLowerCase().includes(q)).slice(0, 40);

  const update = (i: number, patch: Partial<Destination>) => setDests((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  async function start() {
    setBusy(true); setError(null);
    try {
      await api.post('/test-streams', { source, destinations: dests.map((d) => ({ ...d, port: d.port === '' ? 0 : d.port })), loop, stopAfterHours: hours ? Number(hours) : null });
      onStarted();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send a test stream"
      width="max-w-3xl"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!dests.length} onClick={() => void start()}>
            {data?.current && data.current.status !== 'stopped' ? 'Stop the current one and send this' : 'Send'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error && <Alert tone="critical">{error}</Alert>}
        <p className="text-xs text-ink-muted">
          The hub sends the file as it is, paced to real time, with no encoding: a quick way to prove a receiver, a platform or an
          ingest before the real feed. Everything leaving the hub is billed by AWS at {formatUsd(data?.egressUsdPerGb ?? 0.114, 3)} a GB
          (about {formatUsd((mbps / 8) * 3.6 * (data?.egressUsdPerGb ?? 0.114))} an hour per destination at {mbps} Mbit).
        </p>
        {edited && <Alert tone="warning">Passphrases and RTMP stream keys are not kept on the page: type them again before sending.</Alert>}

        <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
          <Field label="Source">
            <Select value={source.kind} onChange={(e) => setSource(e.target.value === 'pattern' ? { kind: 'pattern', id: patterns[0]?.id ?? 'bars-1080p50' } : { kind: 'recording', instanceId: '', session: '' })}>
              <option value="pattern">Test pattern</option>
              <option value="recording">A recording</option>
            </Select>
          </Field>
          {source.kind === 'pattern' ? (
            <Field label="Pattern" hint="Bars and tone with the name and a running counter burnt in; rendered once on the hub (about a minute the first time).">
              <Select value={source.id} onChange={(e) => setSource({ kind: 'pattern', id: e.target.value })}>
                {patterns.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ready ? '' : ' (not rendered yet)'}</option>)}
              </Select>
            </Field>
          ) : (
            <Field label="Recording" hint="Read straight from its parts in S3; sent at the bitrate it was recorded at.">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="find by name or date…" />
              <div className="mt-1 max-h-40 overflow-auto rounded border border-grid">
                {recordings == null ? <div className="p-2 text-xs text-ink-muted">Loading…</div> : shownRecordings.length === 0 ? <div className="p-2 text-xs text-ink-muted">Nothing matches.</div> : shownRecordings.map((r) => {
                  const on = source.kind === 'recording' && source.instanceId === r.instanceId && source.session === r.session;
                  return (
                    <button key={r.prefix} type="button" onClick={() => setSource({ kind: 'recording', instanceId: r.instanceId, session: r.session })}
                      className={`flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-xs hover:bg-raised ${on ? 'bg-series-1/20' : ''}`}>
                      <span className="truncate">{name(r)}</span>
                      <span className="shrink-0 text-ink-muted">{r.session.slice(0, 10)} · {hms(r.seconds * 1000)}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">Destinations</span>
            <Button variant="ghost" disabled={dests.length >= limits.maxDestinations} onClick={() => setDests((ds) => [...ds, newDest(ds.length + 1)])}>+ Add destination</Button>
          </div>
          {dests.map((d, i) => (
            <div key={i} className="space-y-2 rounded border border-grid p-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_180px_auto]">
                <Field label="Label"><Input value={d.label} onChange={(e) => update(i, { label: e.target.value })} /></Field>
                <Field label="Protocol">
                  <Select value={d.protocol} onChange={(e) => update(i, { protocol: e.target.value as Protocol, port: e.target.value === 'srt-listener' ? limits.listenerPorts[0] + i : d.port })}>
                    <option value="srt-caller">SRT caller (the hub calls)</option>
                    <option value="srt-listener">SRT listener (they call the hub)</option>
                    <option value="rtmp">RTMP</option>
                  </Select>
                </Field>
                <div className="flex items-end"><Button variant="ghost" disabled={dests.length === 1} onClick={() => setDests((ds) => ds.filter((_, j) => j !== i))}>Remove</Button></div>
              </div>
              {d.protocol === 'rtmp' ? (
                <Field label="RTMP URL with stream key" hint="rtmp://host/app/streamkey - YouTube, Vimeo, an SRT gateway's RTMP side, anything.">
                  <Input value={d.url} onChange={(e) => update(i, { url: e.target.value })} placeholder="rtmp://a.rtmp.youtube.com/live2/xxxx-xxxx-xxxx" />
                </Field>
              ) : (
                <div className="grid gap-2 sm:grid-cols-4">
                  {d.protocol === 'srt-caller' ? (
                    <Field label="Host"><Input value={d.host} onChange={(e) => update(i, { host: e.target.value })} placeholder="receiver.example.com" /></Field>
                  ) : (
                    <Field label="Listens at" hint={`UDP ${limits.listenerPorts[0]}-${limits.listenerPorts[1]} on the hub`}><Input value={data?.listenHost ?? ''} readOnly /></Field>
                  )}
                  <Field label="Port"><Input type="number" value={d.port} onChange={(e) => update(i, { port: e.target.value === '' ? '' : Number(e.target.value) })} min={d.protocol === 'srt-listener' ? limits.listenerPorts[0] : 1} max={d.protocol === 'srt-listener' ? limits.listenerPorts[1] : 65535} /></Field>
                  <Field label="Stream id" hint="optional"><Input value={d.streamId} onChange={(e) => update(i, { streamId: e.target.value })} /></Field>
                  <Field label="Passphrase" hint="optional, 10+ characters"><Input type="password" value={d.passphrase} onChange={(e) => update(i, { passphrase: e.target.value })} autoComplete="off" /></Field>
                  <Field label="Latency (ms)"><Input type="number" value={d.latencyMs} min={20} max={8000} onChange={(e) => update(i, { latencyMs: Number(e.target.value) })} /></Field>
                </div>
              )}
            </div>
          ))}
          <p className="text-xs text-ink-muted">
            Up to {limits.maxDestinations} destinations and {limits.maxMbps} Mbit/s in all. Each destination is fed by its own thread and retried on its own, so one that is down does not stop the others.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Loop">
            <label className="flex items-center gap-2 pt-1.5 text-xs"><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> Play it again when it ends</label>
          </Field>
          <Field label="Stop after" hint="So a test is never left running overnight.">
            <Select value={hours} onChange={(e) => setHours(e.target.value)}>
              {['0.25', '0.5', '1', '2', '4', '8', '12', '24'].map((h) => <option key={h} value={h}>{Number(h) < 1 ? `${Number(h) * 60} min` : `${h} h`}</option>)}
              <option value="">never</option>
            </Select>
          </Field>
          <div className="pt-5 text-xs text-ink-muted"><Link to="/recordings" className="text-series-1 hover:underline">Recordings</Link></div>
        </div>
      </div>
    </Modal>
  );
}
